import React, { useCallback, useState } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { FormViewer, FormViewerHandle } from '@wix/form-viewer/widget';
import {
  FormError,
  FormStatus as WixFormStatus,
  FormValues,
  FORM_STATUS,
} from '@wix/form-viewer';
import { WaiverForms, WaiverFormStatus } from '../../../../types/form-types';
import { st, classes } from './WaiverForm.st.css';
import { type FormSubmission } from '@wix/ambassador-forms-v4-submission/types';
import { ViewMode } from '../../controller';
import { Header } from '../../Header/header';

export interface WaiverFormProps {
  formId: string;
  waiverForms: WaiverForms;
  formValues: FormValues;
  handleFormChange: (values: FormValues) => void;
  waiverFormRef: React.RefObject<FormViewerHandle>;
  onStatusChange?: (status: WaiverFormStatus) => void;
  onSubmitSuccess?: (submission: FormSubmission) => void;
  onLightboxClose?: () => void;
  viewMode?: ViewMode;
}

const WaiverForm: React.FC<WaiverFormProps> = ({
  formId,
  formValues,
  handleFormChange,
  waiverFormRef,
  waiverForms,
  onStatusChange,
  onSubmitSuccess,
  onLightboxClose,
  viewMode,
}) => {
  const { i18n } = useTranslation();
  const [formErrors, setFormErrors] = useState<FormError[]>([]);

  const handleFormValidate = useCallback((errors: FormError[]) => {
    setFormErrors(errors);
  }, []);

  const handleStatusChanged = useCallback(
    (status: WixFormStatus) => {
      if (status === FORM_STATUS.BUSY) {
        onStatusChange?.(WaiverFormStatus.VALIDATING);
      } else {
        onStatusChange?.(WaiverFormStatus.IDLE);
      }
    },
    [onStatusChange],
  );

  const isOverlay = viewMode === ViewMode.OVERLAY;

  return (
    <div
      data-hook="WaiverForm-main"
      className={st(classes.root, { hasHeader: isOverlay })}
    >
      {isOverlay && <Header onClose={onLightboxClose} />}
      <div className={classes.formContainer}>
        <FormViewer
          onStatusChange={(status) => handleStatusChanged(status)}
          onChange={handleFormChange}
          onValidate={handleFormValidate}
          values={formValues}
          errors={formErrors}
          i18n={i18n}
          formId={formId}
          ref={waiverFormRef}
          overrides={waiverForms.overrides}
          onSubmitSuccess={onSubmitSuccess}
        />
      </div>
    </div>
  );
};

export default WaiverForm;
